import { useCallback, useEffect, useRef, useState } from "react";

import { applyOptimisticMove, buildTree } from "../../caseFiles/navigation";
import type { MoveTarget } from "../../caseFiles/types";
import { WorkspaceTreeAPI } from "./api/workspaceTreeApi";
import type { CreatedDraft, CreatedForm, WsNode } from "./types";

/**
 * Owns the workspace tree for the browser: the nodes and every structural
 * mutation. The same optimistic-then-reconcile shape as `useCaseFilesystem`,
 * over the `/ws` routes.
 *
 * The tree only stores structure; titles and statuses of the items come from
 * `useCaseWorkspace`, which loads the drafts, packet and forms themselves.
 */
export type WorkspaceTree = {
  nodes: WsNode[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createFolder: (
    name: string,
    parentId: string | null
  ) => Promise<WsNode | null>;
  /** "New draft" in `parentId`: custom, or typed (reusing an empty slot). */
  createDraft: (
    parentId: string | null,
    title?: string | null,
    draftType?: string | null
  ) => Promise<CreatedDraft | null>;
  /** "Add Form" in `parentId` (the Forms folder when null), with an optional copy label. */
  createForm: (
    formNumber: string,
    label: string | null,
    parentId: string | null
  ) => Promise<CreatedForm | null>;
  rename: (nodeId: string, name: string) => Promise<void>;
  move: (nodeIds: string[], target: MoveTarget) => Promise<void>;
  remove: (nodeIds: string[]) => Promise<void>;
};

const mergeNodes = (current: WsNode[], updated: WsNode[]): WsNode[] => {
  const byId = new Map(updated.map((node) => [node.id, node]));
  const merged = current.map((node) => byId.get(node.id) ?? node);
  updated.forEach((node) => {
    if (!current.some((existing) => existing.id === node.id)) merged.push(node);
  });
  return merged;
};

export const useWorkspaceTree = (individualId: string): WorkspaceTree => {
  const [nodes, setNodes] = useState<WsNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [loadedFor, setLoadedFor] = useState(individualId);
  if (loadedFor !== individualId) {
    setLoadedFor(individualId);
    setNodes([]);
    setError(null);
    setIsLoading(true);
  }

  // Guards against a slow refresh landing after a newer local mutation.
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    const id = requestId.current + 1;
    requestId.current = id;
    setIsLoading(true);
    try {
      const response = await WorkspaceTreeAPI.getTree(individualId);
      if (requestId.current !== id) return;
      setNodes(response.nodes);
      setError(null);
    } catch (caught) {
      if (requestId.current !== id) return;
      setError(
        caught instanceof Error ? caught.message : "Could not load workspace"
      );
    } finally {
      if (requestId.current === id) setIsLoading(false);
    }
  }, [individualId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Apply a local change now, reconcile with the server, roll back on failure. */
  const mutate = useCallback(
    async (
      optimistic: (current: WsNode[]) => WsNode[],
      commit: () => Promise<void>
    ) => {
      const previous = nodes;
      setNodes(optimistic(previous));
      requestId.current += 1;
      try {
        await commit();
        setError(null);
      } catch (caught) {
        setNodes(previous);
        setError(
          caught instanceof Error ? caught.message : "Change could not be saved"
        );
      }
    },
    [nodes]
  );

  const createFolder = useCallback(
    async (name: string, parentId: string | null) => {
      try {
        const response = await WorkspaceTreeAPI.createFolder(
          individualId,
          name,
          parentId
        );
        setNodes((current) => mergeNodes(current, [response.node]));
        setError(null);
        return response.node;
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not create folder"
        );
        return null;
      }
    },
    [individualId]
  );

  const createDraft = useCallback(
    async (
      parentId: string | null,
      title?: string | null,
      draftType?: string | null
    ) => {
      try {
        const response = await WorkspaceTreeAPI.createDraft(
          individualId,
          parentId,
          title,
          draftType
        );
        setNodes((current) => mergeNodes(current, [response.node]));
        setError(null);
        return response;
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not create draft"
        );
        return null;
      }
    },
    [individualId]
  );

  const createForm = useCallback(
    async (
      formNumber: string,
      label: string | null,
      parentId: string | null
    ) => {
      try {
        const response = await WorkspaceTreeAPI.createForm(
          individualId,
          formNumber,
          label,
          parentId
        );
        setNodes((current) => mergeNodes(current, [response.node]));
        setError(null);
        return response;
      } catch (caught) {
        setError(
          caught instanceof Error ? caught.message : "Could not add form"
        );
        return null;
      }
    },
    [individualId]
  );

  const rename = useCallback(
    (nodeId: string, name: string) =>
      mutate(
        (current) =>
          current.map((node) =>
            // Only folders carry a name; a draft's title lives on the draft.
            node.id === nodeId && node.kind === "folder"
              ? { ...node, name }
              : node
          ),
        async () => {
          const response = await WorkspaceTreeAPI.renameNode(
            individualId,
            nodeId,
            name
          );
          setNodes((current) => mergeNodes(current, [response.node]));
        }
      ),
    [individualId, mutate]
  );

  const move = useCallback(
    (nodeIds: string[], target: MoveTarget) =>
      mutate(
        (current) =>
          applyOptimisticMove(current, nodeIds, target.target_parent_id, {
            beforeId: target.before_id,
            afterId: target.after_id
          }),
        async () => {
          const response = await WorkspaceTreeAPI.moveNodes(
            individualId,
            nodeIds,
            target.target_parent_id,
            { beforeId: target.before_id, afterId: target.after_id }
          );
          // The server owns the real fractional keys; take them so the
          // provisional ones from the optimistic pass do not linger.
          setNodes((current) => mergeNodes(current, response.nodes));
        }
      ),
    [individualId, mutate]
  );

  const remove = useCallback(
    (nodeIds: string[]) => {
      const doomed = new Set(nodeIds);
      const tree = buildTree(nodes);
      const collect = (id: string) => {
        (tree.childrenByParent.get(id) ?? []).forEach((child) => {
          doomed.add(child.id);
          collect(child.id);
        });
      };
      nodeIds.forEach(collect);

      return mutate(
        (current) => current.filter((node) => !doomed.has(node.id)),
        async () => {
          await WorkspaceTreeAPI.deleteNodes(individualId, nodeIds);
        }
      );
    },
    [individualId, mutate, nodes]
  );

  return {
    nodes,
    isLoading,
    error,
    refresh,
    createFolder,
    createDraft,
    createForm,
    rename,
    move,
    remove
  };
};
